/**
 * Per-tenant charged-vs-spent, for the platform dashboard (super_admin).
 *
 * Reads /admin/billing/margin for a window: `revenue` is what we charged the
 * brokerage, against COST TO SERVE — `aws_cost + subscription_cost`, the AWS we
 * attribute to them plus their share of the third-party bills (V080). Not
 * `usage_cost`: that is their usage priced at OUR retail card, so charting it
 * against revenue compares a number with a subset of itself and can never show
 * a tenant we are losing money on, which is the whole question.
 *
 * Both come per tenant from the one request, so the granularity control changes
 * the WINDOW rather than fanning out a request per bucket.
 *
 * Horizontal grouped bars: two series, and brokerage names are long enough that
 * vertical columns would need rotated labels. Colours are the dataviz
 * categorical slots 1-2, validated against this app's own light and dark
 * surfaces (see --rp-chart-* in index.css); identity is never colour-alone —
 * there is a legend, every bar is direct-labelled, and a table view carries the
 * same numbers.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { billingService } from "@re-platform/api-client";
import { Card } from "@re-platform/ui";

import { api } from "../lib/api";

type Grain = "day" | "week" | "month" | "year";

const GRAINS: { id: Grain; label: string }[] = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
  { id: "year", label: "Year" },
];

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** The window [start, end) for the period containing `anchor`. */
function windowFor(anchor: Date, grain: Grain): { start: Date; end: Date } {
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  if (grain === "year") {
    return { start: new Date(y, 0, 1), end: new Date(y + 1, 0, 1) };
  }
  if (grain === "month") {
    return { start: new Date(y, m, 1), end: new Date(y, m + 1, 1) };
  }
  if (grain === "week") {
    // Week starts Monday — an invoice week that starts mid-week reads as an
    // arbitrary slice when compared against the next one.
    const day = (anchor.getDay() + 6) % 7;
    const start = new Date(y, m, anchor.getDate() - day);
    return { start, end: new Date(y, m, start.getDate() + 7) };
  }
  const start = new Date(y, m, anchor.getDate());
  return { start, end: new Date(y, m, anchor.getDate() + 1) };
}

function step(anchor: Date, grain: Grain, delta: -1 | 1): Date {
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  const d = anchor.getDate();
  if (grain === "year") return new Date(y + delta, m, 1);
  if (grain === "month") return new Date(y, m + delta, 1);
  if (grain === "week") return new Date(y, m, d + 7 * delta);
  return new Date(y, m, d + delta);
}

function periodLabel(anchor: Date, grain: Grain): string {
  const { start, end } = windowFor(anchor, grain);
  const last = new Date(end.getTime() - 86_400_000);
  if (grain === "year") return String(start.getFullYear());
  if (grain === "month")
    return start.toLocaleDateString(undefined, {
      month: "long",
      year: "numeric",
    });
  if (grain === "day")
    return start.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  const fmt = (x: Date) =>
    x.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${fmt(start)} – ${fmt(last)}, ${last.getFullYear()}`;
}

function usd(v: string | number): string {
  const n = typeof v === "number" ? v : parseFloat(v);
  if (isNaN(n)) return "—";
  return n >= 1000
    ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : `$${n.toFixed(2)}`;
}

const SERIES = [
  { key: "charged", label: "Charged", color: "var(--rp-chart-1)" },
  { key: "spent", label: "Cost to serve", color: "var(--rp-chart-2)" },
] as const;

export function TenantSpendChart() {
  const [grain, setGrain] = useState<Grain>("month");
  const [anchor, setAnchor] = useState(() => new Date());
  const [asTable, setAsTable] = useState(false);
  const [hover, setHover] = useState<string | null>(null);

  const { start, end } = windowFor(anchor, grain);
  const since = iso(start);
  const until = iso(end);

  const q = useQuery({
    queryKey: ["billing", "margin", since, until],
    queryFn: () => billingService.getMargin(api, { since, until }),
    retry: false,
    staleTime: 60_000,
  });

  const rows = useMemo(() => {
    const src = q.data?.rows ?? [];
    return (
      [...src]
        .map((r) => ({
          id: r.tenant_id,
          name: r.display_name,
          charged: parseFloat(r.revenue) || 0,
          spent: parseFloat(r.usage_cost) || 0,
        }))
        // Biggest first: a magnitude comparison is read top-down, and an
        // unordered list makes the eye do the sorting.
        .sort(
          (a, b) => Math.max(b.charged, b.spent) - Math.max(a.charged, a.spent),
        )
    );
  }, [q.data]);

  const max = Math.max(1, ...rows.flatMap((r) => [r.charged, r.spent]));

  // The current period is the newest one worth showing; stepping past it would
  // only ever return zeros.
  const atLatest =
    windowFor(new Date(), grain).start.getTime() <= start.getTime();

  const years = useMemo(() => {
    const thisYear = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => thisYear - i);
  }, []);

  return (
    <Card padding="lg">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 4,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
          Charged vs spent
        </h2>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            type="button"
            className="rp-btn rp-btn--secondary rp-btn--sm"
            aria-pressed={asTable}
            onClick={() => setAsTable((t) => !t)}
          >
            {asTable ? "Chart" : "Table"}
          </button>
        </span>
      </div>
      <p
        style={{
          color: "var(--rp-color-text-muted)",
          fontSize: 13,
          margin: "0 0 14px",
        }}
      >
        What each brokerage was billed, against what serving them cost us —
        attributed AWS plus their share of the third-party subscriptions.
      </p>

      {/* Filters in one row above the chart. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <span style={{ display: "inline-flex", gap: 4 }}>
          {GRAINS.map((g) => (
            <button
              key={g.id}
              type="button"
              aria-pressed={grain === g.id}
              onClick={() => setGrain(g.id)}
              style={{
                border: "1px solid var(--rp-color-border)",
                background:
                  grain === g.id
                    ? "var(--rp-color-surface-2)"
                    : "var(--rp-color-surface)",
                color:
                  grain === g.id
                    ? "var(--rp-color-text)"
                    : "var(--rp-color-text-muted)",
                fontWeight: grain === g.id ? 700 : 400,
                borderRadius: "var(--rp-radius-sm)",
                padding: "5px 11px",
                // fontFamily, not the `font` shorthand: fontWeight below is
                // conditional, and React warns when a rerender changes a
                // longhand while a conflicting shorthand is also set.
                fontFamily: "inherit",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              {g.label}
            </button>
          ))}
        </span>

        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            className="rp-btn rp-btn--secondary rp-btn--sm"
            aria-label="Previous period"
            onClick={() => setAnchor((a) => step(a, grain, -1))}
          >
            ‹
          </button>
          <span
            style={{ fontSize: 13, minWidth: 150, textAlign: "center" }}
            aria-live="polite"
          >
            {periodLabel(anchor, grain)}
          </span>
          <button
            type="button"
            className="rp-btn rp-btn--secondary rp-btn--sm"
            aria-label="Next period"
            disabled={atLatest}
            onClick={() => setAnchor((a) => step(a, grain, 1))}
          >
            ›
          </button>
        </span>

        <select
          aria-label="Year"
          value={anchor.getFullYear()}
          onChange={(e) => {
            const y = Number(e.target.value);
            const now = new Date();
            // Jumping to a PAST year lands on its final period, which is the
            // one with data; jumping to this year lands on today.
            setAnchor(y === now.getFullYear() ? now : new Date(y, 11, 31));
          }}
          style={{
            border: "1px solid var(--rp-color-border)",
            borderRadius: "var(--rp-radius-sm)",
            background: "var(--rp-color-surface)",
            color: "var(--rp-color-text)",
            padding: "5px 8px",
            font: "inherit",
            fontSize: 13,
          }}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {/* Legend — always present for two series. */}
      <div
        style={{
          display: "flex",
          gap: 16,
          marginBottom: 12,
          fontSize: 12.5,
          color: "var(--rp-color-text-muted)",
        }}
      >
        {SERIES.map((s) => (
          <span
            key={s.key}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span
              aria-hidden
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: s.color,
              }}
            />
            {s.label}
          </span>
        ))}
      </div>

      {q.isPending && (
        <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13 }}>
          Loading…
        </p>
      )}
      {q.isError && (
        <p style={{ color: "var(--rp-color-danger)", fontSize: 13 }}>
          {q.error instanceof Error
            ? q.error.message
            : "Could not load billing figures"}
        </p>
      )}
      {!q.isPending && !q.isError && rows.length === 0 && (
        <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13 }}>
          Nothing billed in this period.
        </p>
      )}

      {rows.length > 0 &&
        (asTable ? (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13.5,
              }}
            >
              <thead>
                <tr
                  style={{
                    textAlign: "left",
                    color: "var(--rp-color-text-muted)",
                    fontSize: 11,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                  }}
                >
                  <th style={{ padding: "8px 10px" }}>Brokerage</th>
                  <th style={{ padding: "8px 10px" }}>Charged</th>
                  <th style={{ padding: "8px 10px" }}>Spent</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    style={{ borderTop: "1px solid var(--rp-color-border)" }}
                  >
                    <td style={{ padding: "8px 10px" }}>{r.name}</td>
                    <td style={{ padding: "8px 10px" }}>{usd(r.charged)}</td>
                    <td style={{ padding: "8px 10px" }}>{usd(r.spent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {rows.map((r) => (
              <div key={r.id}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {r.name}
                  </span>
                  {/* The reason this chart exists. Status colour + a word, never
                      colour alone, and distinct from the two series hues. */}
                  {r.spent > r.charged && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: "var(--rp-color-danger-faint)",
                        color: "var(--rp-color-danger)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      At a loss
                    </span>
                  )}
                </div>
                {SERIES.map((s) => {
                  const value = s.key === "charged" ? r.charged : r.spent;
                  const id = `${r.id}:${s.key}`;
                  return (
                    <div
                      key={s.key}
                      onMouseEnter={() => setHover(id)}
                      onMouseLeave={() => setHover(null)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        // 2px of surface between the two touching bars.
                        marginBottom: s.key === "charged" ? 2 : 0,
                        cursor: "default",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          height: 18,
                          background:
                            hover === id
                              ? "var(--rp-color-surface-2)"
                              : "transparent",
                          borderRadius: 4,
                        }}
                      >
                        <div
                          role="img"
                          aria-label={`${r.name}, ${s.label} ${usd(value)}`}
                          style={{
                            width: `${Math.max((value / max) * 100, value > 0 ? 1.5 : 0)}%`,
                            height: "100%",
                            background: s.color,
                            // Square at the baseline, rounded at the data end.
                            borderRadius: "0 4px 4px 0",
                          }}
                        />
                      </div>
                      {/* Direct label on every bar: two series, few rows, and
                          it removes colour as the only carrier of identity. */}
                      <span
                        style={{
                          fontSize: 12.5,
                          color: "var(--rp-color-text-muted)",
                          minWidth: 78,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {usd(value)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ))}
    </Card>
  );
}
